/**
 * integrationsRouter — RPC contract for the integrations router, the settings
 * domain's one deployed callable over the integration registry
 * (firebase/src/settings/integrations.router.ts). Actions ride it, each an
 * `action` on the payload: `saveIntegration` proves and stores one
 * integration's credential, `listLettermintRoutes` discovers the routes a
 * saved Lettermint team token reaches, `readEffectiveAccounts` answers which
 * Lettermint account and Revolut environment the organization runs on now.
 * `beginRevolutConnection` starts a one-use connection attempt and
 * `finishRevolutConnection` completes it in the initiating member's session;
 * `readRevolutConnections` reads its configuration and credential status.
 *
 * EVERY ARM COMPOSES FROM ITS OWN CONTRACT FILE. `listLettermintRoutes` and
 * `readEffectiveAccounts` are flat objects and spread onto this level through
 * `.extend()`, keeping their `.strict()`. `saveIntegration`'s input is itself
 * a union discriminated on `id`, which a discriminated union cannot carry as
 * an option, so it travels whole under `save`.
 *
 * `action` IS ADDRESSING, NOT DATA: the router takes it back off before the
 * payload reaches a handler. The envelope asserts no permission: saving and
 * route discovery want `workspace.credentials.manage`, the account read
 * `tickets.events.manage`, each checked inside its handler.
 *
 * Fields justified:
 *   action — selects the handler, and which payload shape applies.
 *   save   — saveIntegration's own input, whole.
 *   (every other field) — the matching act's own fields.
 */
import { z } from "zod";
import {
	BeginRevolutConnectionInput,
	BeginRevolutConnectionOutput,
} from "./beginRevolutConnection.action.ts";
import {
	FinishRevolutConnectionInput,
	FinishRevolutConnectionOutput,
} from "./finishRevolutConnection.action.ts";
import {
	ListLettermintRoutesInput,
	ListLettermintRoutesOutput,
} from "./listLettermintRoutes.action.ts";
import {
	ReadEffectiveAccountsInput,
	ReadEffectiveAccountsOutput,
} from "./readEffectiveAccounts.action.ts";
import {
	ReadRevolutConnectionsInput,
	ReadRevolutConnectionsOutput,
} from "./readRevolutConnections.action.ts";
import {
	RequestRevolutSandboxConfirmationInput,
	RequestRevolutSandboxConfirmationOutput,
} from "./requestRevolutSandboxConfirmation.action.ts";
import {
	SaveIntegrationInput,
	SaveIntegrationOutcome,
} from "./saveIntegration.action.ts";

export const IntegrationsInput = z.discriminatedUnion("action", [
	BeginRevolutConnectionInput.extend({
		action: z.literal("beginRevolutConnection"),
	}),
	FinishRevolutConnectionInput.extend({
		action: z.literal("finishRevolutConnection"),
	}),
	ReadRevolutConnectionsInput.extend({
		action: z.literal("readRevolutConnections"),
	}),
	RequestRevolutSandboxConfirmationInput.extend({
		action: z.literal("requestRevolutSandboxConfirmation"),
	}),
	z
		.object({
			action: z.literal("saveIntegration"),
			save: SaveIntegrationInput,
		})
		.strict(),
	ListLettermintRoutesInput.extend({
		action: z.literal("listLettermintRoutes"),
	}),
	ReadEffectiveAccountsInput.extend({
		action: z.literal("readEffectiveAccounts"),
	}),
]);
export type IntegrationsInput = z.infer<typeof IntegrationsInput>;

export const IntegrationsOutput = z.union([
	BeginRevolutConnectionOutput,
	FinishRevolutConnectionOutput,
	ReadRevolutConnectionsOutput,
	RequestRevolutSandboxConfirmationOutput,
	SaveIntegrationOutcome,
	ListLettermintRoutesOutput,
	ReadEffectiveAccountsOutput,
]);
export type IntegrationsOutput = z.infer<typeof IntegrationsOutput>;
